// src/components/Skeleton.tsx
import React from "react";

interface SkeletonProps {
  className?: string;
  variant?:
    | "text"
    | "circle"
    | "rectangle"
    | "card"
    | "table"
    | "avatar"
    | "button"
    | "badge"
    | "input"
    | "stat";
  width?: string | number;
  height?: string | number;
  animation?: "pulse" | "wave" | "none";
  count?: number;
  theme?: "light" | "dark";
  color?: "blue" | "green" | "red" | "yellow" | "purple" | "gray" | "default";
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = "",
  variant = "text",
  width,
  height,
  animation = "pulse",
  count = 1,
  theme,
  color = "default",
}) => {
  // Determine if we should use theme-aware or default classes
  const getBaseClasses = () => {
    if (theme) {
      if (color === "blue") {
        return theme === "dark"
          ? "bg-gradient-to-r from-blue-900/30 via-blue-800/30 to-blue-900/30"
          : "bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50";
      } else if (color === "green") {
        return theme === "dark"
          ? "bg-gradient-to-r from-green-900/30 via-green-800/30 to-green-900/30"
          : "bg-gradient-to-r from-green-50 via-green-100 to-green-50";
      } else if (color === "red") {
        return theme === "dark"
          ? "bg-gradient-to-r from-red-900/30 via-red-800/30 to-red-900/30"
          : "bg-gradient-to-r from-red-50 via-red-100 to-red-50";
      } else if (color === "yellow") {
        return theme === "dark"
          ? "bg-gradient-to-r from-yellow-900/30 via-yellow-800/30 to-yellow-900/30"
          : "bg-gradient-to-r from-yellow-50 via-yellow-100 to-yellow-50";
      } else if (color === "purple") {
        return theme === "dark"
          ? "bg-gradient-to-r from-purple-900/30 via-purple-800/30 to-purple-900/30"
          : "bg-gradient-to-r from-purple-50 via-purple-100 to-purple-50";
      }
      return theme === "dark"
        ? "bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700"
        : "bg-gradient-to-r from-sky-50 via-blue-50 to-sky-50";
    }
    return "bg-gray-200 dark:bg-gray-700";
  };

  const getAnimationClasses = () => {
    const baseClasses = {
      pulse: "animate-pulse",
      wave: "animate-shimmer",
      none: "",
    };

    if (animation === "wave" && !theme) {
      return "animate-shimmer bg-gradient-to-r from-transparent via-gray-300/50 to-transparent dark:via-gray-600/50";
    }

    return baseClasses[animation];
  };

  const variantClasses = {
    text: "h-4 rounded",
    circle: "rounded-full",
    rectangle: "",
    card: "rounded-lg h-48",
    table: "h-10 rounded",
    avatar: "rounded-full w-10 h-10",
    button: "h-10 rounded-md",
    badge: "h-6 rounded-full",
    input: "h-10 rounded-md",
    stat: "h-20 rounded-lg",
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === "number" ? `${width}px` : width;
  if (height)
    style.height = typeof height === "number" ? `${height}px` : height;

  const skeletonElement = (
    <div
      className={`
        ${getBaseClasses()}
        ${variantClasses[variant]}
        ${getAnimationClasses()}
        ${className}
      `}
      style={style}
      aria-busy="true"
      aria-label="Loading..."
    />
  );

  if (count > 1) {
    return (
      <>
        {Array.from({ length: count }).map((_, index) => (
          <React.Fragment key={index}>
            {skeletonElement}
            {index < count - 1 && variant === "text" && <div className="h-2" />}
          </React.Fragment>
        ))}
      </>
    );
  }

  return skeletonElement;
};

// Original Compound Components
interface SkeletonTextProps {
  lines?: number;
  lastLineWidth?: string;
  className?: string;
  spacing?: string;
  theme?: "light" | "dark";
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  lastLineWidth = "60%",
  className = "",
  spacing = "space-y-2",
  theme,
}) => {
  return (
    <div className={`${spacing} ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={index === lines - 1 ? lastLineWidth : "w-full"}
          variant="text"
          animation="pulse"
          theme={theme}
        />
      ))}
    </div>
  );
};

interface SkeletonCardProps {
  className?: string;
  withImage?: boolean;
  withFooter?: boolean;
  lines?: number;
  theme?: "light" | "dark";
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  className = "",
  withImage = true,
  withFooter = true,
  lines = 3,
  theme,
}) => {
  const borderClass = theme === "dark" ? "border-gray-800" : "border-gray-200";

  return (
    <div className={`p-4 rounded-xl border ${borderClass} ${className}`}>
      <div className="space-y-4">
        {withImage && (
          <Skeleton
            variant="rectangle"
            className="w-full h-40 rounded-lg"
            animation="pulse"
            theme={theme}
          />
        )}
        <div className="flex items-center space-x-3">
          <Skeleton variant="avatar" animation="pulse" theme={theme} />
          <div className="space-y-2 flex-1">
            <Skeleton variant="text" animation="pulse" theme={theme} />
            <Skeleton
              variant="text"
              width="60%"
              animation="pulse"
              theme={theme}
            />
          </div>
        </div>
        <SkeletonText lines={lines} className="mt-2" theme={theme} />
        {withFooter && (
          <div className="flex space-x-2">
            <Skeleton
              variant="button"
              width={80}
              animation="pulse"
              theme={theme}
            />
            <Skeleton
              variant="button"
              width={60}
              animation="pulse"
              theme={theme}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  withHeader?: boolean;
  className?: string;
  theme?: "light" | "dark";
}

export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 4,
  withHeader = true,
  className = "",
  theme,
}) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {withHeader && (
        <div className="flex space-x-2">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton
              key={`header-${index}`}
              variant="text"
              className="flex-1 h-8"
              animation="pulse"
              theme={theme}
            />
          ))}
        </div>
      )}
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex space-x-2">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={`cell-${rowIndex}-${colIndex}`}
                variant="text"
                className="flex-1 h-10"
                animation="pulse"
                theme={theme}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

interface SkeletonListProps {
  items?: number;
  className?: string;
  theme?: "light" | "dark";
}

export const SkeletonList: React.FC<SkeletonListProps> = ({
  items = 5,
  className = "",
  theme,
}) => {
  const borderClass = theme === "dark" ? "border-gray-800" : "border-gray-200";

  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: items }).map((_, index) => (
        <div
          key={index}
          className={`flex items-center space-x-3 p-3 border rounded-lg ${borderClass}`}
        >
          <Skeleton variant="avatar" animation="pulse" theme={theme} />
          <div className="flex-1 space-y-2">
            <Skeleton
              variant="text"
              width="70%"
              animation="pulse"
              theme={theme}
            />
            <Skeleton
              variant="text"
              width="40%"
              animation="pulse"
              theme={theme}
            />
          </div>
          <Skeleton
            variant="button"
            width={60}
            animation="pulse"
            theme={theme}
          />
        </div>
      ))}
    </div>
  );
};

interface SkeletonDashboardProps {
  stats?: number;
  className?: string;
  theme?: "light" | "dark";
}

export const SkeletonDashboard: React.FC<SkeletonDashboardProps> = ({
  stats = 4,
  className = "",
  theme,
}) => {
  const borderClass = theme === "dark" ? "border-gray-800" : "border-gray-200";

  return (
    <div className={className}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: stats }).map((_, index) => (
          <div key={index} className={`p-4 rounded-lg border ${borderClass}`}>
            <div className="flex justify-between items-start">
              <div className="space-y-2 flex-1">
                <Skeleton
                  variant="text"
                  width="40%"
                  animation="pulse"
                  theme={theme}
                />
                <Skeleton
                  variant="text"
                  width="60%"
                  animation="pulse"
                  className="h-6"
                  theme={theme}
                />
              </div>
              <Skeleton
                variant="circle"
                width={40}
                height={40}
                animation="pulse"
                theme={theme}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`p-4 rounded-lg border space-y-4 ${borderClass}`}>
          <Skeleton
            variant="text"
            width="30%"
            animation="pulse"
            className="h-6"
            theme={theme}
          />
          <SkeletonList items={3} theme={theme} />
        </div>
        <div className={`p-4 rounded-lg border space-y-4 ${borderClass}`}>
          <Skeleton
            variant="text"
            width="30%"
            animation="pulse"
            className="h-6"
            theme={theme}
          />
          <SkeletonText lines={4} theme={theme} />
        </div>
      </div>
    </div>
  );
};

// ===== JOB LISTING COMPONENTS =====

// Job Listing Specific Skeletons
interface SkeletonJobCardProps {
  count?: number;
  theme?: "light" | "dark";
  withBookmark?: boolean;
  withApplyButton?: boolean;
  withCompanyLogo?: boolean;
  className?: string;
}

export const SkeletonJobCard: React.FC<SkeletonJobCardProps> = ({
  count = 1,
  theme = "light",
  withBookmark = true,
  withApplyButton = true,
  withCompanyLogo = true,
  className = "",
}) => {
  const bgClass =
    theme === "dark"
      ? "bg-gray-800 border-gray-700"
      : "bg-white border-sky-100";

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={`rounded-xl shadow p-6 border flex flex-col h-full relative ${bgClass} ${className}`}
        >
          {/* Bookmark skeleton */}
          {withBookmark && (
            <div className="absolute top-4 right-4">
              <Skeleton
                variant="circle"
                width={40}
                height={40}
                animation="pulse"
                theme={theme}
              />
            </div>
          )}

          <div className="flex-1">
            {/* Company logo/name skeleton */}
            {withCompanyLogo && (
              <div className="flex items-center mb-4">
                <Skeleton
                  variant="circle"
                  width={48}
                  height={48}
                  animation="pulse"
                  theme={theme}
                  className="mr-3"
                />
                <div className="flex-1">
                  <Skeleton
                    variant="text"
                    width="70%"
                    animation="pulse"
                    theme={theme}
                    className="mb-2"
                  />
                  <Skeleton
                    variant="text"
                    width="50%"
                    animation="pulse"
                    theme={theme}
                  />
                </div>
              </div>
            )}

            {/* Job title skeleton */}
            <Skeleton
              variant="text"
              width="85%"
              animation="pulse"
              theme={theme}
              className="h-6 mb-3"
            />

            {/* Job details skeleton */}
            <div className="space-y-2 mb-4">
              <Skeleton
                variant="text"
                width="60%"
                animation="pulse"
                theme={theme}
              />
              <Skeleton
                variant="text"
                width="50%"
                animation="pulse"
                theme={theme}
              />
              <Skeleton
                variant="text"
                width="40%"
                animation="pulse"
                theme={theme}
              />
            </div>

            {/* Description skeleton */}
            <div className="space-y-2 mb-4">
              <Skeleton variant="text" animation="pulse" theme={theme} />
              <Skeleton variant="text" animation="pulse" theme={theme} />
              <Skeleton
                variant="text"
                width="80%"
                animation="pulse"
                theme={theme}
              />
            </div>

            {/* Category tags skeleton */}
            <div className="flex gap-2 mb-4">
              <Skeleton
                variant="badge"
                width={60}
                animation="pulse"
                theme={theme}
                className="h-6"
              />
              <Skeleton
                variant="badge"
                width={80}
                animation="pulse"
                theme={theme}
                className="h-6"
              />
            </div>
          </div>

          {/* Apply button skeleton */}
          {withApplyButton && (
            <div className="mt-4">
              <Skeleton
                variant="button"
                animation="pulse"
                theme={theme}
                className="w-full h-12"
              />
            </div>
          )}
        </div>
      ))}
    </>
  );
};

interface SkeletonJobListingProps {
  items?: number;
  theme?: "light" | "dark";
  layout?: "grid" | "list";
}

export const SkeletonJobListing: React.FC<SkeletonJobListingProps> = ({
  items = 6,
  theme = "light",
  layout = "grid",
}) => {
  return (
    <div
      className={
        layout === "grid" ? "grid md:grid-cols-2 gap-8 mb-8" : "space-y-8 mb-8"
      }
    >
      <SkeletonJobCard
        count={items}
        theme={theme}
        withBookmark={true}
        withApplyButton={true}
        withCompanyLogo={true}
      />
    </div>
  );
};

interface SkeletonJobSearchProps {
  theme?: "light" | "dark";
}

export const SkeletonJobSearch: React.FC<SkeletonJobSearchProps> = ({
  theme = "light",
}) => {
  return (
    <div className="flex flex-col md:flex-row items-center gap-4 mb-10">
      <Skeleton
        variant="input"
        animation="pulse"
        theme={theme}
        className="w-full md:w-2/3 h-12"
      />
      <Skeleton
        variant="input"
        animation="pulse"
        theme={theme}
        className="w-full md:w-1/3 h-12"
      />
    </div>
  );
};

interface SkeletonPaginationProps {
  theme?: "light" | "dark";
}

export const SkeletonPagination: React.FC<SkeletonPaginationProps> = ({
  theme = "light",
}) => {
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8">
      {/* Page info skeleton */}
      <Skeleton
        variant="text"
        width={120}
        animation="pulse"
        theme={theme}
        className="h-4"
      />

      {/* Pagination buttons skeleton */}
      <div className="flex items-center gap-2">
        {Array.from({ length: 7 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="button"
            width={index === 3 ? 40 : 32}
            animation="pulse"
            theme={theme}
            className="h-10"
          />
        ))}
      </div>
    </div>
  );
};

// ===== JOB FORM COMPONENTS =====

interface SkeletonJobFormProps {
  theme?: "light" | "dark";
  className?: string;
  mode?: "create" | "edit";
}

export const SkeletonJobForm: React.FC<SkeletonJobFormProps> = ({
  theme = "light",
  className = "",
}) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-gray-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-gray-50";
  const headerBgClass = theme === "dark" ? "bg-gray-900" : "bg-white";

  return (
    <div
      className={`min-h-screen p-4 sm:p-6 ${className} ${
        theme === "dark"
          ? "bg-gradient-to-br from-gray-900 to-gray-800"
          : "bg-gradient-to-br from-green-50 to-sky-50"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <Skeleton
            variant="text"
            width="60%"
            animation="pulse"
            theme={theme}
            className="h-8 mb-2 mx-auto"
          />
          <Skeleton
            variant="text"
            width="40%"
            animation="pulse"
            theme={theme}
            className="h-4 mx-auto"
          />
        </div>

        {/* Form Card */}
        <div
          className={`shadow-lg rounded-xl sm:rounded-2xl overflow-hidden border ${borderClass} ${bgClass}`}
        >
          {/* Form Header */}
          <div className="px-4 sm:px-8 py-4 sm:py-6 bg-gradient-to-r from-green-500 to-sky-500">
            <Skeleton
              variant="text"
              width="50%"
              animation="pulse"
              theme="light"
              className="h-6"
            />
          </div>

          {/* Form Content */}
          <div className="p-4 sm:p-6 lg:p-8">
            {/* Image Upload Section */}
            <div className="mb-8">
              <Skeleton
                variant="text"
                width="40%"
                animation="pulse"
                theme={theme}
                className="h-5 mb-2"
              />
              <Skeleton
                variant="text"
                width="70%"
                animation="pulse"
                theme={theme}
                className="h-3 mb-4"
              />

              {/* Image Upload Box */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 sm:p-8 w-full max-w-md mx-auto ${
                  theme === "dark"
                    ? "border-green-600 bg-gray-700/50"
                    : "border-green-200"
                }`}
              >
                <Skeleton
                  variant="circle"
                  width={48}
                  height={48}
                  animation="pulse"
                  theme={theme}
                  className="mx-auto"
                />
                <Skeleton
                  variant="text"
                  width="40%"
                  animation="pulse"
                  theme={theme}
                  className="h-4 mt-4 mx-auto"
                />
                <Skeleton
                  variant="text"
                  width="60%"
                  animation="pulse"
                  theme={theme}
                  className="h-3 mt-2 mx-auto"
                />
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-6 sm:space-y-8">
              {/* Basic Information Grid */}
              <div>
                <Skeleton
                  variant="text"
                  width="30%"
                  animation="pulse"
                  theme={theme}
                  className="h-5 mb-4"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {[1, 2, 3, 4, 5, 6].map((field) => (
                    <div key={field}>
                      <Skeleton
                        variant="text"
                        width="40%"
                        animation="pulse"
                        theme={theme}
                        className="h-4 mb-2"
                      />
                      <Skeleton
                        variant="input"
                        animation="pulse"
                        theme={theme}
                        className="h-10"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Job Details Grid */}
              <div>
                <Skeleton
                  variant="text"
                  width="30%"
                  animation="pulse"
                  theme={theme}
                  className="h-5 mb-4"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {[1, 2, 3].map((field) => (
                    <div key={field}>
                      <Skeleton
                        variant="text"
                        width="40%"
                        animation="pulse"
                        theme={theme}
                        className="h-4 mb-2"
                      />
                      <Skeleton
                        variant="input"
                        animation="pulse"
                        theme={theme}
                        className="h-10"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Deadline Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <Skeleton
                    variant="text"
                    width="50%"
                    animation="pulse"
                    theme={theme}
                    className="h-4 mb-2"
                  />
                  <Skeleton
                    variant="input"
                    animation="pulse"
                    theme={theme}
                    className="h-10"
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    animation="pulse"
                    theme={theme}
                    className="h-3 mt-1"
                  />
                </div>
              </div>

              {/* Description Section */}
              <div>
                <Skeleton
                  variant="text"
                  width="40%"
                  animation="pulse"
                  theme={theme}
                  className="h-5 mb-2"
                />
                <Skeleton
                  variant="text"
                  animation="pulse"
                  theme={theme}
                  className="h-20 rounded-lg"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-6 sm:pt-8 mt-6 sm:mt-8">
              {[1, 2].map((btn) => (
                <Skeleton
                  key={btn}
                  variant="button"
                  width="100%"
                  animation="pulse"
                  theme={theme}
                  className="h-12"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Quick Tips Section */}
        <div className="mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {[1, 2].map((tip) => (
            <div
              key={tip}
              className={`rounded-lg border p-4 sm:p-6 ${borderClass} ${headerBgClass}`}
            >
              <Skeleton
                variant="text"
                width="40%"
                animation="pulse"
                theme={theme}
                className="h-5 mb-3"
              />
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((item) => (
                  <Skeleton
                    key={item}
                    variant="text"
                    width={`${70 + item * 5}%`}
                    animation="pulse"
                    theme={theme}
                    className="h-3"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface SkeletonJobLoadingProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonJobLoading: React.FC<SkeletonJobLoadingProps> = ({
  theme = "light",
  className = "",
}) => {
  return (
    <div
      className={`min-h-screen p-4 sm:p-6 ${className} ${
        theme === "dark"
          ? "bg-gradient-to-br from-gray-900 to-gray-800"
          : "bg-gradient-to-br from-green-50 to-sky-50"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <Skeleton
            variant="circle"
            width={48}
            height={48}
            animation="pulse"
            theme={theme}
            className="mx-auto mb-4"
          />
          <Skeleton
            variant="text"
            width="40%"
            animation="pulse"
            theme={theme}
            className="h-6 mx-auto mb-2"
          />
          <Skeleton
            variant="text"
            width="60%"
            animation="pulse"
            theme={theme}
            className="h-4 mx-auto"
          />
          <Skeleton
            variant="text"
            width="50%"
            animation="pulse"
            theme={theme}
            className="h-3 mx-auto mt-2"
          />
        </div>
      </div>
    </div>
  );
};

interface SkeletonJobErrorProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonJobError: React.FC<SkeletonJobErrorProps> = ({
  theme = "light",
  className = "",
}) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-gray-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-white";

  return (
    <div
      className={`min-h-screen p-4 sm:p-6 ${className} ${
        theme === "dark"
          ? "bg-gradient-to-br from-gray-900 to-gray-800"
          : "bg-gradient-to-br from-green-50 to-sky-50"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div
            className={`border rounded-lg max-w-md mx-auto p-4 sm:p-6 ${borderClass} ${bgClass}`}
          >
            <Skeleton
              variant="text"
              width="60%"
              animation="pulse"
              theme={theme}
              className="h-6 mx-auto mb-3"
            />
            <Skeleton
              variant="text"
              width="80%"
              animation="pulse"
              theme={theme}
              className="h-4 mx-auto mb-4"
            />
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {[1, 2].map((btn) => (
                <Skeleton
                  key={btn}
                  variant="button"
                  width={120}
                  animation="pulse"
                  theme={theme}
                  className="h-10"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== SUBSCRIPTION COMPONENTS =====

interface SkeletonSubscriptionCardProps {
  count?: number;
  theme?: "light" | "dark";
  isPopular?: boolean;
  hasBadge?: boolean;
  hasSavings?: boolean;
  withFeatures?: boolean;
  className?: string;
}

export const SkeletonSubscriptionCard: React.FC<
  SkeletonSubscriptionCardProps
> = ({
  count = 1,
  theme = "light",
  isPopular = false,
  hasBadge = false,
  hasSavings = false,
  withFeatures = true,
  className = "",
}) => {
  const bgClass =
    theme === "dark"
      ? "bg-gray-800 border-gray-700"
      : "bg-white border-sky-100";

  const popularBorderClass = isPopular
    ? theme === "dark"
      ? "border-sky-500"
      : "border-sky-300"
    : "";

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={`relative rounded-2xl shadow-lg border-2 transition-all duration-300 ${bgClass} ${popularBorderClass} ${className}`}
        >
          {/* Popular Badge Skeleton */}
          {hasBadge && (
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
              <Skeleton
                variant="badge"
                width={120}
                animation="pulse"
                theme={theme}
                className="h-8"
              />
            </div>
          )}

          {/* Savings Badge Skeleton */}
          {hasSavings && (
            <div className="absolute -top-4 right-4 z-10">
              <Skeleton
                variant="badge"
                width={80}
                animation="pulse"
                theme={theme}
                className="h-8"
              />
            </div>
          )}

          <div className="p-6 sm:p-8">
            {/* Package Name */}
            <Skeleton
              variant="text"
              width="60%"
              animation="pulse"
              theme={theme}
              className="h-7 mb-3"
            />

            {/* Package Description */}
            <div className="mb-4">
              <Skeleton
                variant="text"
                width="90%"
                animation="pulse"
                theme={theme}
                className="h-4 mb-2"
              />
              <Skeleton
                variant="text"
                width="70%"
                animation="pulse"
                theme={theme}
                className="h-4"
              />
            </div>

            {/* Price Section */}
            <div className="mb-4">
              <div className="flex items-baseline">
                <Skeleton
                  variant="text"
                  width="40%"
                  animation="pulse"
                  theme={theme}
                  className="h-10"
                />
                <Skeleton
                  variant="text"
                  width="20%"
                  animation="pulse"
                  theme={theme}
                  className="h-5 ml-2"
                />
              </div>
              {/* Original Price */}
              <Skeleton
                variant="text"
                width="30%"
                animation="pulse"
                theme={theme}
                className="h-4 mt-1"
              />
            </div>

            {/* Package Details */}
            <div className="mb-4">
              <Skeleton
                variant="text"
                width="50%"
                animation="pulse"
                theme={theme}
                className="h-4"
              />
            </div>

            {/* Features List */}
            {withFeatures && (
              <ul className="space-y-3 mb-6 sm:mb-8">
                {[1, 2, 3, 4].map((featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <Skeleton
                      variant="circle"
                      width={18}
                      height={18}
                      animation="pulse"
                      theme={theme}
                      className="mr-3 mt-0.5"
                    />
                    <Skeleton
                      variant="text"
                      width={featureIndex % 2 === 0 ? "80%" : "90%"}
                      animation="pulse"
                      theme={theme}
                      className="h-4"
                    />
                  </li>
                ))}
              </ul>
            )}

            {/* Action Button */}
            <Skeleton
              variant="button"
              animation="pulse"
              theme={theme}
              className="w-full h-12"
            />
          </div>
        </div>
      ))}
    </>
  );
};

interface SkeletonSubscriptionPlansProps {
  count?: number;
  theme?: "light" | "dark";
  layout?: "single" | "double" | "triple";
  showBillingToggle?: boolean;
  showWhitelistCard?: boolean;
}

export const SkeletonSubscriptionPlans: React.FC<
  SkeletonSubscriptionPlansProps
> = ({
  count = 3,
  theme = "light",
  layout = "triple",
  showBillingToggle = true,
  showWhitelistCard = true,
}) => {
  const gridClass = {
    single: "grid-cols-1 max-w-2xl mx-auto",
    double: "grid-cols-1 md:grid-cols-2",
    triple: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  }[layout];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-12">
        <Skeleton
          variant="text"
          width="40%"
          animation="pulse"
          theme={theme}
          className="h-10 mx-auto mb-4"
        />
        <Skeleton
          variant="text"
          width="60%"
          animation="pulse"
          theme={theme}
          className="h-6 mx-auto mb-6 sm:mb-8"
        />

        {/* Billing Toggle Skeleton */}
        {showBillingToggle && (
          <div className="flex justify-center sm:justify-end mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Skeleton
                variant="text"
                width={60}
                animation="pulse"
                theme={theme}
                className="h-6"
              />
              <div className="flex gap-2">
                <Skeleton
                  variant="button"
                  width={100}
                  animation="pulse"
                  theme={theme}
                  className="h-10 rounded-full"
                />
                <Skeleton
                  variant="button"
                  width={120}
                  animation="pulse"
                  theme={theme}
                  className="h-10 rounded-full"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Packages Grid */}
      <div className={`grid ${gridClass} gap-6 sm:gap-8 mb-8 sm:mb-12`}>
        <SkeletonSubscriptionCard
          count={count}
          theme={theme}
          isPopular={layout === "triple" ? count > 1 : false}
          hasBadge={true}
          hasSavings={true}
          withFeatures={true}
        />
      </div>

      {/* Whitelist Card Skeleton */}
      {showWhitelistCard && (
        <div
          className={`rounded-2xl shadow-lg border p-6 sm:p-8 text-center ${
            theme === "dark"
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-sky-50"
          }`}
        >
          <div className="flex justify-center mb-4">
            <Skeleton
              variant="circle"
              width={40}
              height={40}
              animation="pulse"
              theme={theme}
            />
          </div>
          <Skeleton
            variant="text"
            width="50%"
            animation="pulse"
            theme={theme}
            className="h-7 mx-auto mb-3"
          />
          <Skeleton
            variant="text"
            width="90%"
            animation="pulse"
            theme={theme}
            className="h-4 mx-auto mb-2"
          />
          <Skeleton
            variant="text"
            width="80%"
            animation="pulse"
            theme={theme}
            className="h-4 mx-auto mb-6"
          />
          <Skeleton
            variant="button"
            width={200}
            animation="pulse"
            theme={theme}
            className="h-12 mx-auto"
          />
        </div>
      )}
    </div>
  );
};

// ===== ADMIN PACKAGES COMPONENTS =====

interface SkeletonAdminPackageStatsProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonAdminPackageStats: React.FC<
  SkeletonAdminPackageStatsProps
> = ({ theme = "light", className = "" }) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-gray-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-white";

  return (
    <div
      className={`grid grid-cols-2 md:grid-cols-6 gap-3 sm:gap-4 mb-6 ${className}`}
    >
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className={`p-3 sm:p-4 rounded-lg shadow border ${borderClass} ${bgClass}`}
        >
          <Skeleton
            variant="text"
            width="60%"
            animation="pulse"
            theme={theme}
            className="mb-2"
          />
          <Skeleton
            variant="text"
            width="40%"
            animation="pulse"
            theme={theme}
            className="h-6"
          />
        </div>
      ))}
    </div>
  );
};

interface SkeletonAdminPackageCardProps {
  count?: number;
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonAdminPackageCard: React.FC<
  SkeletonAdminPackageCardProps
> = ({ count = 1, theme = "light", className = "" }) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-gray-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-white";

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={`rounded-lg shadow border hover:shadow-md transition-shadow ${borderClass} ${bgClass} ${className}`}
        >
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div className="flex-1 w-full">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                  <Skeleton
                    variant="text"
                    width="70%"
                    animation="pulse"
                    theme={theme}
                    className="h-6"
                  />
                  <div className="flex flex-wrap gap-1">
                    <Skeleton
                      variant="badge"
                      width={60}
                      animation="pulse"
                      theme={theme}
                      className="h-5"
                    />
                    <Skeleton
                      variant="badge"
                      width={50}
                      animation="pulse"
                      theme={theme}
                      className="h-5"
                    />
                  </div>
                </div>
                <Skeleton
                  variant="text"
                  width="90%"
                  animation="pulse"
                  theme={theme}
                  className="mb-2"
                />
                <Skeleton
                  variant="text"
                  width="60%"
                  animation="pulse"
                  theme={theme}
                />
              </div>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-start sm:justify-end">
                {[1, 2, 3, 4].map((btn) => (
                  <Skeleton
                    key={btn}
                    variant="button"
                    width={btn === 1 ? 100 : 80}
                    animation="pulse"
                    theme={theme}
                    className="h-10"
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4">
              {[1, 2].map((plan) => (
                <div
                  key={plan}
                  className={`p-4 rounded-lg ${theme === "dark" ? "bg-gray-700" : "bg-gray-50"}`}
                >
                  <Skeleton
                    variant="text"
                    width="40%"
                    animation="pulse"
                    theme={theme}
                    className="mb-3 h-5"
                  />
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Skeleton
                        variant="text"
                        width="30%"
                        animation="pulse"
                        theme={theme}
                      />
                      <Skeleton
                        variant="text"
                        width="40%"
                        animation="pulse"
                        theme={theme}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Skeleton
                        variant="text"
                        width="40%"
                        animation="pulse"
                        theme={theme}
                      />
                      <Skeleton
                        variant="text"
                        width="30%"
                        animation="pulse"
                        theme={theme}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className={`p-4 rounded-lg mb-4 ${theme === "dark" ? "bg-gray-700" : "bg-gray-50"}`}
            >
              <Skeleton
                variant="text"
                width="50%"
                animation="pulse"
                theme={theme}
                className="mb-3 h-5"
              />
              <div className="space-y-2">
                <Skeleton variant="text" animation="pulse" theme={theme} />
                <Skeleton variant="text" animation="pulse" theme={theme} />
                <Skeleton variant="text" animation="pulse" theme={theme} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

interface SkeletonAdminPackageFormProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonAdminPackageForm: React.FC<
  SkeletonAdminPackageFormProps
> = ({ theme = "light", className = "" }) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-gray-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-gray-50";

  return (
    <div
      className={`p-4 sm:p-6 rounded-lg border ${borderClass} ${bgClass} ${className}`}
    >
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <Skeleton
          variant="text"
          width="60%"
          animation="pulse"
          theme={theme}
          className="h-8"
        />
        <Skeleton
          variant="circle"
          width={24}
          height={24}
          animation="pulse"
          theme={theme}
        />
      </div>

      <div className="space-y-6 sm:space-y-8 mb-6 sm:mb-8">
        {/* Basic Information Section */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-sm border ${borderClass} ${theme === "dark" ? "bg-gray-700" : "bg-white"}`}
        >
          <Skeleton
            variant="text"
            width="40%"
            animation="pulse"
            theme={theme}
            className="mb-4 h-6"
          />

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2].map((field) => (
                <div key={field}>
                  <Skeleton
                    variant="text"
                    width="40%"
                    animation="pulse"
                    theme={theme}
                    className="mb-2"
                  />
                  <Skeleton
                    variant="input"
                    animation="pulse"
                    theme={theme}
                    className="h-12"
                  />
                </div>
              ))}
            </div>

            <div>
              <Skeleton
                variant="text"
                width="40%"
                animation="pulse"
                theme={theme}
                className="mb-2"
              />
              <Skeleton
                variant="input"
                animation="pulse"
                theme={theme}
                className="h-12"
              />
            </div>

            <div>
              <Skeleton
                variant="text"
                width="40%"
                animation="pulse"
                theme={theme}
                className="mb-2"
              />
              <Skeleton
                variant="input"
                animation="pulse"
                theme={theme}
                className="h-12"
              />
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-sm border ${borderClass} ${theme === "dark" ? "bg-gray-700" : "bg-white"}`}
        >
          <Skeleton
            variant="text"
            width="30%"
            animation="pulse"
            theme={theme}
            className="mb-4 h-6"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((field) => (
              <div key={field}>
                <Skeleton
                  variant="text"
                  width="50%"
                  animation="pulse"
                  theme={theme}
                  className="mb-2"
                />
                <Skeleton
                  variant="input"
                  animation="pulse"
                  theme={theme}
                  className="h-12"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Features Section */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-sm border ${borderClass} ${theme === "dark" ? "bg-gray-700" : "bg-white"}`}
        >
          <Skeleton
            variant="text"
            width="30%"
            animation="pulse"
            theme={theme}
            className="mb-4 h-6"
          />

          <div className="space-y-4">
            <div className="flex gap-2">
              <Skeleton
                variant="input"
                animation="pulse"
                theme={theme}
                className="flex-1 h-12"
              />
              <Skeleton
                variant="button"
                width={100}
                animation="pulse"
                theme={theme}
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              {[1, 2, 3].map((feature) => (
                <Skeleton
                  key={feature}
                  variant="text"
                  animation="pulse"
                  theme={theme}
                  className="h-10"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 sm:pt-6">
        {[1, 2].map((btn) => (
          <Skeleton
            key={btn}
            variant="button"
            width={120}
            animation="pulse"
            theme={theme}
            className="h-12"
          />
        ))}
      </div>
    </div>
  );
};

interface SkeletonAdminPackageSearchProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonAdminPackageSearch: React.FC<
  SkeletonAdminPackageSearchProps
> = ({ theme = "light", className = "" }) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-gray-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-white";

  return (
    <div
      className={`p-4 rounded-lg shadow border mb-6 ${borderClass} ${bgClass} ${className}`}
    >
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Skeleton
            variant="input"
            animation="pulse"
            theme={theme}
            className="h-12"
          />
        </div>
        <div className="w-full sm:w-48">
          <Skeleton
            variant="input"
            animation="pulse"
            theme={theme}
            className="h-12"
          />
        </div>
        <div className="w-full sm:w-auto">
          <div className="flex items-center gap-2 p-2">
            <Skeleton
              variant="circle"
              width={20}
              height={20}
              animation="pulse"
              theme={theme}
            />
            <Skeleton
              variant="text"
              width={120}
              animation="pulse"
              theme={theme}
            />
          </div>
        </div>
      </div>
      <Skeleton
        variant="text"
        width={200}
        animation="pulse"
        theme={theme}
        className="mt-4"
      />
    </div>
  );
};

// ===== EVENT MANAGEMENT COMPONENTS =====

interface SkeletonEventManagerHeaderProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonEventManagerHeader: React.FC<
  SkeletonEventManagerHeaderProps
> = ({ theme = "light", className = "" }) => {
  //   const borderClass = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';
  //   const bgClass = theme === 'dark' ? 'bg-gray-800' : 'bg-white';

  return (
    <div
      className={`flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 ${className}`}
    >
      <div className="mb-4 lg:mb-0">
        <Skeleton
          variant="text"
          width="300px"
          animation="pulse"
          theme={theme}
          className="h-10 mb-2"
        />
        <Skeleton
          variant="text"
          width="200px"
          animation="pulse"
          theme={theme}
          className="h-5"
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <Skeleton
          variant="button"
          width={120}
          animation="pulse"
          theme={theme}
          className="h-10"
        />
        <Skeleton
          variant="button"
          width={140}
          animation="pulse"
          theme={theme}
          className="h-10"
        />
      </div>
    </div>
  );
};

interface SkeletonEventFilterBarProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonEventFilterBar: React.FC<SkeletonEventFilterBarProps> = ({
  theme = "light",
  className = "",
}) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-blue-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-white";

  return (
    <div
      className={`rounded-xl p-6 mb-6 border ${borderClass} ${bgClass} shadow-lg ${className}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Skeleton
            variant="input"
            animation="pulse"
            theme={theme}
            className="w-full h-12"
          />
        </div>
        <Skeleton
          variant="input"
          animation="pulse"
          theme={theme}
          className="h-12"
        />
      </div>
    </div>
  );
};

interface SkeletonEventTableRowProps {
  theme?: "light" | "dark";
  withActions?: boolean;
}

export const SkeletonEventTableRow: React.FC<SkeletonEventTableRowProps> = ({
  theme = "light",
  withActions = true,
}) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-blue-100";
  const hoverClass =
    theme === "dark" ? "hover:bg-gray-750" : "hover:bg-blue-50";

  return (
    <tr className={`border-t ${borderClass} ${hoverClass}`}>
      {/* Event Column */}
      <td className="px-6 py-4">
        <div>
          <Skeleton
            variant="text"
            width="180px"
            animation="pulse"
            theme={theme}
            className="h-6 mb-2"
          />
          <Skeleton
            variant="text"
            width="250px"
            animation="pulse"
            theme={theme}
            className="h-4"
          />
        </div>
      </td>

      {/* Type Column */}
      <td className="px-6 py-4">
        <Skeleton
          variant="badge"
          width={80}
          animation="pulse"
          theme={theme}
          className="h-6"
        />
      </td>

      {/* Date & Time Column */}
      <td className="px-6 py-4">
        <div className="space-y-1">
          <Skeleton
            variant="text"
            width={100}
            animation="pulse"
            theme={theme}
            className="h-4"
          />
          <Skeleton
            variant="text"
            width={80}
            animation="pulse"
            theme={theme}
            className="h-3"
          />
        </div>
      </td>

      {/* Audience Column */}
      <td className="px-6 py-4">
        <Skeleton
          variant="badge"
          width={90}
          animation="pulse"
          theme={theme}
          className="h-6"
        />
      </td>

      {/* Registrations Column */}
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <Skeleton
            variant="circle"
            width={16}
            height={16}
            animation="pulse"
            theme={theme}
          />
          <Skeleton
            variant="text"
            width={50}
            animation="pulse"
            theme={theme}
            className="h-4"
          />
        </div>
      </td>

      {/* Status Column */}
      <td className="px-6 py-4">
        <Skeleton
          variant="input"
          width={120}
          animation="pulse"
          theme={theme}
          className="h-8"
        />
      </td>

      {/* Actions Column */}
      {withActions && (
        <td className="px-6 py-4">
          <div className="flex space-x-2">
            <Skeleton
              variant="circle"
              width={32}
              height={32}
              animation="pulse"
              theme={theme}
              color="blue"
            />
            <Skeleton
              variant="circle"
              width={32}
              height={32}
              animation="pulse"
              theme={theme}
              color="green"
            />
            <Skeleton
              variant="circle"
              width={32}
              height={32}
              animation="pulse"
              theme={theme}
              color="red"
            />
          </div>
        </td>
      )}
    </tr>
  );
};

interface SkeletonEventTableProps {
  rows?: number;
  theme?: "light" | "dark";
  withHeader?: boolean;
  className?: string;
}

export const SkeletonEventTable: React.FC<SkeletonEventTableProps> = ({
  rows = 5,
  theme = "light",
  withHeader = true,
  className = "",
}) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-blue-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-white";
  const headerBgClass = theme === "dark" ? "bg-gray-700" : "bg-blue-50";

  return (
    <div
      className={`rounded-xl overflow-hidden border ${borderClass} ${bgClass} shadow-lg ${className}`}
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          {withHeader && (
            <thead>
              <tr className={headerBgClass}>
                {[
                  "Event",
                  "Type",
                  "Date & Time",
                  "Audience",
                  "Registrations",
                  "Status",
                  "Actions",
                ].map((header) => (
                  <th
                    key={header}
                    className="px-6 py-4 text-left font-semibold"
                  >
                    <Skeleton
                      variant="text"
                      width={header === "Date & Time" ? 100 : 80}
                      animation="pulse"
                      theme={theme}
                      className="h-5"
                    />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {Array.from({ length: rows }).map((_, index) => (
              <SkeletonEventTableRow
                key={index}
                theme={theme}
                withActions={true}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface SkeletonEventEmptyStateProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonEventEmptyState: React.FC<
  SkeletonEventEmptyStateProps
> = ({ theme = "light", className = "" }) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-blue-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-white";

  return (
    <div
      className={`rounded-xl border ${borderClass} ${bgClass} shadow-lg p-8 text-center ${className}`}
    >
      <Skeleton
        variant="circle"
        width={48}
        height={48}
        animation="pulse"
        theme={theme}
        className="mx-auto mb-4 opacity-50"
      />
      <Skeleton
        variant="text"
        width={120}
        animation="pulse"
        theme={theme}
        className="h-6 mx-auto mb-2"
      />
      <Skeleton
        variant="text"
        width={200}
        animation="pulse"
        theme={theme}
        className="h-4 mx-auto"
      />
    </div>
  );
};

interface SkeletonEventManagerLoadingProps {
  theme?: "light" | "dark";
  className?: string;
}

export const SkeletonEventManagerLoading: React.FC<
  SkeletonEventManagerLoadingProps
> = ({ theme = "light", className = "" }) => {
  const bgGradient =
    theme === "dark"
      ? "bg-gradient-to-br from-gray-900 to-gray-800"
      : "bg-gradient-to-br from-sky-50 to-blue-50";

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${bgGradient} ${className}`}
    >
      <div className="max-w-7xl mx-auto">
        <SkeletonEventManagerHeader theme={theme} />
        <SkeletonEventFilterBar theme={theme} />
        <SkeletonEventTable rows={5} theme={theme} withHeader={true} />
      </div>
    </div>
  );
};

interface SkeletonEventErrorStateProps {
  theme?: "light" | "dark";
  className?: string;
  children?: React.ReactNode;
}

export const SkeletonEventErrorState: React.FC<
  SkeletonEventErrorStateProps
> = ({ theme = "light", className = "" }) => {
  const borderClass = theme === "dark" ? "border-gray-700" : "border-red-200";
  const bgClass = theme === "dark" ? "bg-gray-800" : "bg-white";
  const textColor = theme === "dark" ? "text-red-400" : "text-red-600";
  const bgGradient =
    theme === "dark"
      ? "bg-gradient-to-br from-gray-900 to-gray-800"
      : "bg-gradient-to-br from-sky-50 to-blue-50";

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${bgGradient} ${className}`}
    >
      <div className="max-w-7xl mx-auto text-center">
        <div
          className={`rounded-xl border p-8 max-w-2xl mx-auto ${borderClass} ${bgClass}`}
        >
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Skeleton
              variant="circle"
              width={64}
              height={64}
              animation="pulse"
              theme={theme}
              color="red"
            />
          </div>
          <Skeleton
            variant="text"
            width="60%"
            animation="pulse"
            theme={theme}
            className={`h-8 mx-auto mb-4 ${textColor}`}
          />
          <Skeleton
            variant="text"
            width="80%"
            animation="pulse"
            theme={theme}
            className="h-5 mx-auto mb-6"
          />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Skeleton
              variant="button"
              width={120}
              animation="pulse"
              theme={theme}
              className="h-10"
            />
            <Skeleton
              variant="button"
              width={120}
              animation="pulse"
              theme={theme}
              className="h-10"
              color="gray"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Export default Skeleton
export default Skeleton;
